import Vue from 'vue';
import { MutationTree } from 'vuex';

import { State } from '@/manager/convocation/store/state';
import { Convocation } from '@/manager/convocation/convocation.model';
import { Note } from '@/manager/convocation/note.model';
import { Log } from '@/manager/convocation/log.model';

export interface Mutations {
    newConvocation: { convocation: Convocation };
    updateConvocation: { oldValue: Convocation, newValue: Convocation };
    setConvocationsList: {list: Convocation[]};
    setNextConvocationList: {list: Convocation[]};
    updateConvocationHeaders: {
        idConvocation: string,
        totalParticipantSummoned: boolean,
        totalParticipantConfirmed: boolean,
        totalParticipantChecked: boolean,
        totalParticipantOut: boolean,
        totalParticipantPostpone: boolean,
        totalParticipantNotConfirmed: boolean,
        totalParticipantNotConfirmedEnrolled: boolean,
    };
    newParticipantNote: { note: Note };
    updateParticipantNote: { participantId: string, note: Note };
    removeParticipantNotes: { participantId: string };
    newParticipantLog: { log: Log };
    addParticipantListRequest: { requestId: number, requestSource: any };
    removeParticipantListRequest: { requestId: number, requestSource: any };
}

export const mutations: MutationTree<State> = {
    setConvocationsList: (state, payload) => {
        state.list = payload;
    },
    setConvocationNotes: (state, payload) => {
        state.convocationNoteList = payload;
    },
    setNextConvocationList: (state, payload) => {
        state.nextConvocationList = payload;
    },
    setPaymentStatusList: (state, payload) => {
        state.paymentStatusList = payload;
    },
    setParticipantList: (state, payload) => {
        state.participantList = payload;
    },
    setWaitingList: (state, payload) => {
        state.waitingList = payload;
    },
    setCountWaitingList: (state, payload) => {
        state.countWaitingList = payload;
    },
    setPostponeList: (state, payload) => {
        state.pendingPostpone = payload;
    },
    setCountPostponeList: (state, payload) => {
        state.countPostponeList = payload;
    },
    setRepeaterList: (state, payload) => {
        state.repeaterList = payload;
    },
    setTeamList: (state, payload) => {
        state.teamList = payload;
    },
    setTagList: (state, payload) => {
        state.tagList = payload;
    },
    newConvocation: (state, payload) => {
        state.list.push(payload);
    },
    newConvocationNote: (state, payload) => {
        state.convocationNoteList.push(payload);
    },
    setConvocationsStatesList: (state, payload) => {
        state.convocationsStatesList.push(payload);
    },
    removeParticipantNotes: (state, payload) => {
        const participantNotes = state.participantNoteList.filter((note: Note) => {
            return (note.participantId === payload.participantId);
        });
        participantNotes.forEach((participantNote) => {
            const index = state.participantNoteList.findIndex(
                (note: Note) => {
                    return note.participantId === participantNote.participantId;
                },
            );
            state.participantNoteList.splice(index, 1);
        });
    },
    removeConvocationNote: (state, noteId) => {
        const index = state.convocationNoteList.map( (item: any) => item.id ).indexOf( noteId );
        if (index > -1) {
            state.convocationNoteList.splice(index, 1);
        }
    },
    removeParticipantNote: (state, noteId) => {
        const index = state.participantNoteList.map( (item: any) => item.id ).indexOf( noteId );
        if (index > -1) {
            state.participantNoteList.splice(index, 1);
        }
    },
    setParticipantNotes: (state, payload) => {
        const participantNoteListIds = state.participantNoteList.map( (item: any) => item.id );
        payload.notes.forEach((note: Note) => {
            const newNote = Object.assign({}, note, { ['participantId']: payload.participantId });
            const index = participantNoteListIds.indexOf( note.id );
            if (index > -1) {
                state.participantNoteList.splice( index, 1, newNote );
            } else {
                state.participantNoteList.push( newNote );
            }
        });
    },
    setParticipantLog: (state, payload) => {
        const participantLog = state.participantLogList.find(
            (item: any) =>
                item.participantId === payload.participantId,
        );
        const log = payload.log;
        const newLog = Object.assign({}, log, { ['participantId']: payload.participantId });

        let index = -1;
        if (participantLog !== undefined) {
            index = state.participantLogList.indexOf( participantLog );
        }

        if (index > -1) {
            state.participantLogList.splice( index, 1, newLog );
        } else {
            state.participantLogList.push( newLog );
        }
    },
    setParticipantPostponementLog: (state, payload) => {
        const participantPostponementLog = state.participantPostponementLogList.find(
            (item: any) =>
                item.participantId === payload.participantId,
        );
        const log = payload.log;
        const newLog = Object.assign({}, log, { ['participantId']: payload.participantId });

        let index = -1;
        if (participantPostponementLog !== undefined) {
            index = state.participantPostponementLogList.indexOf( participantPostponementLog );
        }

        if (index > -1) {
            state.participantPostponementLogList.splice( index, 1, newLog );
        } else {
            state.participantPostponementLogList.push( newLog );
        }
    },
    setParticipantTraspasementLog: (state, payload) => {
        const participantTraspasementLog = state.participantTraspasementLogList.find(
            (item: any) =>
                item.participantId === payload.participantId,
        );
        const log = payload.log;
        const newLog = Object.assign({}, log, { ['participantId']: payload.participantId });

        let index = -1;
        if (participantTraspasementLog !== undefined) {
            index = state.participantTraspasementLogList.indexOf( participantTraspasementLog );
        }

        if (index > -1) {
            state.participantTraspasementLogList.splice( index, 1, newLog );
        } else {
            state.participantTraspasementLogList.push( newLog );
        }
    },
    newParticipant: (state, payload) => {
        state.participantList.push(payload);
    },
    newParticipantNote: (state, payload) => {
        state.participantNoteList.push(payload);
    },
    newParticipantLog: (state, payload) => {
        state.participantLogList.push(payload);
    },
    updateConvocation: (state, payload) => {
        payload.oldValue = payload.newValue;
    },
    updateConvocationHeaders: (state, payload) => {
        const item = state.list.find((item: any) => item.id === payload.idConvocation);
        if (item) {
            item.totalParticipantSummoned = payload.totalParticipantSummoned;
            item.totalParticipantConfirmed = payload.totalParticipantConfirmed;
            item.totalParticipantChecked = payload.totalParticipantChecked;
            item.totalParticipantOut = payload.totalParticipantOut;
            item.totalParticipantPostpone = payload.totalParticipantPostpone;
            item.totalParticipantNotConfirmed = payload.totalParticipantNotConfirmed;
            item.totalParticipantAbandonment = payload.totalParticipantAbandonment;
            item.totalParticipantEnrolled = payload.totalParticipantEnrolled;
        }
    },
    updateParticipant: (state, payload) => {
        const index = state.participantList.map( (item: any) => item.id ).indexOf( payload.id );
        if (index > -1) {
            const participant = state.participantList[ index ];
            const newParticipant = Object.assign({}, participant, payload);
            Vue.set( state.participantList, index, newParticipant );
        }
    },
    updateParticipantField: (state, payload) => {
        const index = state.participantList.map( (item: any) => item.id ).indexOf( payload.id );
        if (index > -1) {
            const participant = state.participantList[ index ];
            if (!participant.checked) {
                const newParticipant = Object.assign({}, participant, payload);
                Vue.set(state.participantList, index, newParticipant);
            }
        }
    },
    updateConvocationNote: (state, payload) => {
        const index = state.convocationNoteList.map( (item: any) => item.id ).indexOf( payload.id );
        if (index > -1) {
            const note = state.convocationNoteList[ index ];
            const newNote = Object.assign({}, note, payload);
            state.convocationNoteList.splice( index, 1, newNote );
        }
    },
    updateParticipantNote: (state, payload) => {
        const index = state.participantNoteList.map( (item: any) => item.id ).indexOf( payload.id );
        if (index > -1) {
            const note = state.participantNoteList[ index ];
            const newNote = Object.assign({}, note, payload);
            state.participantNoteList.splice( index, 1, newNote );
        }
    },
    addParticipantListRequest: (state, payload) => {
        state.participantListRequests.push( payload );
    },
    removeParticipantListRequest: (state, payload) => {
        const index = state.participantListRequests.map( (item: any) => item.requestId ).indexOf( payload.requestId );
        if (index > -1) {
            state.participantListRequests.splice(index, 1);
        }
    },
    emptyParticipantListRequest: (state) => {
        state.participantListRequests.forEach( (participantListRequest: any) => {
            participantListRequest.requestSource.cancel();
        } );
        state.participantListRequests = [];
    },
    setParticipantMassiveEmailList: (state, payload) => {
        state.participantMassiveEmailList = payload;
    },
};
